import { ComponentProps, ReactElement, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { CancelButton } from 'src/components/Buttons';
import { Content, useContent } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { Button } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';

type AddCustomPaymentTypeFormValues = {
  name: string;
};

export type AddCustomPaymentTypeDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  isOpen: boolean;
  onSave?: (name: string) => void | Promise<void>;
  onCancel?: () => void;
};

export function AddCustomPaymentTypeDialog({
  isOpen,
  onSave,
  onCancel,
  ...genericDialogProps
}: AddCustomPaymentTypeDialogProps): ReactElement | null {
  const requiredText = useContent(ContentId.Required);

  const {
    register,
    reset,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<AddCustomPaymentTypeFormValues>({
    defaultValues: { name: '' },
  });

  const onSubmit = useCallback(
    async (data: AddCustomPaymentTypeFormValues) => {
      await onSave?.(data.name);
    },
    [onSave]
  );

  const handleSaveClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      isOpen={isOpen}
      size="sm"
      header={<Content id={ContentId.AddPaymentType} />}
      footer={
        <>
          <CancelButton disabled={isSubmitting} onClick={onCancel} />
          <Button
            variant={'regular'}
            disabled={isSubmitting}
            onClick={handleSaveClick}
          >
            <Content id={ContentId.Save} />
          </Button>
        </>
      }
      onClosed={() => {
        reset();
      }}
      {...genericDialogProps}
      onCancel={onCancel}
    >
      <PosFormField
        errors={errors.name?.message}
        label={<Content id={ContentId.PaymentTypeName} />}
      >
        <PosTextField
          rootProps={{
            disabled: isSubmitting,
            state: getTextFieldState(errors.name),
          }}
          disabled={isSubmitting}
          {...register('name', {
            required: { value: true, message: requiredText },
            maxLength: {
              value: 100,
              message: 'Maximum 100 characters',
            },
          })}
        />
      </PosFormField>
    </GenericDialog>
  );
}
